import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

const RECONCILE_COOLDOWN_MS = 30_000
const lastReconcileAt = new Map()

function currentUserKey() {
  return document.querySelector("[data-notification-badge-user-id-value]")
    ?.dataset.notificationBadgeUserIdValue || "anonymous"
}

// Replaces turbo_stream_from for comments pages.
//
// turbo_stream_from / TurboCableStreamSourceElement has an unreliable
// subscription lifecycle on web (but not on native), causing real-time comment
// broadcasts to be missed. This controller uses the same direct ActionCable
// subscription pattern as discovery_controller.js (known to work on both
// platforms) and pipes received HTML through Turbo.renderStreamMessage.
//
// Usage: add data-controller="comments-cable"
//        and data-comments-cable-signed-stream-value="SIGNED_NAME" to
//        the page wrapper.  The signed stream name is computed server-side
//        via Turbo::StreamsChannel.signed_stream_name([stream_string]).
export default class extends Controller {
  static values = { signedStream: String, reconcileUrl: String }

  connect() {
    if (!this.signedStreamValue) return

    this._subscription = consumer.subscriptions.create(
      { channel: "Turbo::StreamsChannel", signed_stream_name: this.signedStreamValue },
      {
        connected: () => this._reconcile(),
        received: (data) => this._received(data)
      }
    )
  }

  disconnect() {
    this._subscription?.unsubscribe()
  }

  _received(data) {
    if (typeof data === "string" && window.Turbo) {
      window.Turbo.renderStreamMessage(data)
    }
  }

  // Cable broadcasts are transient. Re-read the visible photo cells after every
  // initial connection or reconnect so reveals missed while offline self-heal.
  async _reconcile() {
    if (!this.hasReconcileUrlValue) return
    const now = Date.now()
    const userKey = currentUserKey()
    if (this._reconciling || now - (lastReconcileAt.get(userKey) || 0) < RECONCILE_COOLDOWN_MS) return

    lastReconcileAt.set(userKey, now)
    this._reconciling = true

    try {
      const photoCells = Array.from(this.element.querySelectorAll('[id^="their-photo-"]'))
      const cardStates = Array.from(this.element.querySelectorAll('[id^="my-reveal-state-"]'))
      const commentSections = Array.from(this.element.querySelectorAll('[id^="discovery-feed-comment-section-"]'))
      const ids = Array.from(new Set([
        ...photoCells.map((element) => element.id.replace("their-photo-", "")),
        ...cardStates.map((element) => element.id.replace("my-reveal-state-", "")),
        ...commentSections.map((element) => element.id.replace("discovery-feed-comment-section-", ""))
      ])).filter((id) => /^\d+$/.test(id))

      const photoStates = new Map(photoCells.map((element) => [
        element.id.replace("their-photo-", ""),
        element.dataset.photoState || ""
      ]))
      const myRevealStates = new Map(cardStates.map((element) => [
        element.id.replace("my-reveal-state-", ""),
        element.dataset.myRevealState || ""
      ]))
      const commentStates = new Map(commentSections.map((element) => [
        element.id.replace("discovery-feed-comment-section-", ""),
        element.dataset.commentCount || "0"
      ]))

      for (let offset = 0; offset < ids.length; offset += 50) {
        const batchIds = ids.slice(offset, offset + 50)
        const url = new URL(this.reconcileUrlValue, window.location.origin)
        url.searchParams.set("ids", batchIds.join(","))
        url.searchParams.set("photo_states", batchIds.filter((id) => photoStates.has(id)).map((id) => `${id}:${photoStates.get(id)}`).join(","))
        url.searchParams.set("card_states", batchIds.filter((id) => myRevealStates.has(id)).map((id) => `${id}:${myRevealStates.get(id)}`).join(","))
        url.searchParams.set("comment_states", batchIds.filter((id) => commentStates.has(id)).map((id) => `${id}:${commentStates.get(id)}`).join(","))

        try {
          const response = await fetch(url, {
            headers: { "Accept": "text/vnd.turbo-stream.html" },
            credentials: "same-origin"
          })
          if (!response.ok) continue

          const stream = await response.text()
          if (stream.trim() && window.Turbo) window.Turbo.renderStreamMessage(stream)
        } catch (error) {
          console.warn("[comments-cable] reveal reconciliation failed", error)
        }
      }
    } finally {
      this._reconciling = false
    }
  }
}
